/**
 * Funnel analytics for THE BUILT INDEX™.
 *
 * Every step of the funnel emits one consistently named event so drop-off is
 * measurable end to end:
 *
 *   lead_gate_view      — the lead gate was shown
 *   lead_gate_submit    — name/email captured, session created
 *   assessment_built    — BUILT principles assessment completed
 *   assessment_disc     — leadership style assessment completed
 *   assessment_love     — fuel & drain assessment completed
 *   assessment_lovin    — listening assessment completed (scores submitted)
 *   results_view        — results page reached (locked or unlocked)
 *   checkout_start      — paid unlock checkout opened
 *   unlock              — payment confirmed, report unlocked
 *   report_download     — PDF report downloaded
 *   book_delivery       — free book opened / delivered
 *
 * Each step is mirrored to the analytics layer (GA4/GTM via trackEvent) AND
 * persisted to `built_index_events` so the admin dashboard can report on the
 * funnel and export it without depending on a third-party tool.
 */
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/tracking";
import { attributionProps } from "@/lib/utm";

export const BUILT_INDEX_STEPS = [
  "lead_gate_view",
  "lead_gate_submit",
  "assessment_built",
  "assessment_disc",
  "assessment_love",
  "assessment_lovin",
  "results_view",
  "checkout_start",
  "unlock",
  "report_download",
  "book_delivery",
] as const;

export type BuiltIndexStep = (typeof BUILT_INDEX_STEPS)[number];

export const BUILT_INDEX_STEP_LABELS: Record<BuiltIndexStep, string> = {
  lead_gate_view: "Lead gate viewed",
  lead_gate_submit: "Lead captured",
  assessment_built: "BUILT completed",
  assessment_disc: "Leadership style completed",
  assessment_love: "Fuel & drain completed",
  assessment_lovin: "Listening completed",
  results_view: "Results viewed",
  checkout_start: "Checkout started",
  unlock: "Unlocked (paid)",
  report_download: "Report downloaded",
  book_delivery: "Book delivered",
};

type Meta = Record<string, string | number | boolean | null | undefined>;

/** In-session de-duplication so a re-render never double-counts a step. */
const seen = new Set<string>();

export function resetBuiltIndexStepDedupe() {
  seen.clear();
}

export function trackBuiltIndexStep(
  step: BuiltIndexStep,
  options: { sessionId?: string | null; email?: string | null; metadata?: Meta; once?: boolean } = {},
) {
  const { sessionId = null, email = null, metadata = {}, once = true } = options;
  const key = `${step}:${sessionId ?? "anon"}`;
  if (once) {
    if (seen.has(key)) return;
    seen.add(key);
  }

  const path = typeof window === "undefined" ? undefined : window.location.pathname;
  trackEvent("built_index_step", { step, session_id: sessionId ?? undefined, ...metadata });

  // Fire-and-forget: analytics must never block or break the funnel.
  void supabase
    .from("built_index_events")
    .insert({
      session_id: sessionId ?? undefined,
      step,
      email: email ? email.trim().toLowerCase() : undefined,
      path,
      metadata: metadata as Record<string, unknown>,
      attribution: attributionProps() as unknown as Record<string, unknown>,
      user_agent: typeof navigator === "undefined" ? undefined : navigator.userAgent.slice(0, 400),
    } as never)
    .then(({ error }) => {
      if (error && import.meta.env.DEV) {
        console.debug("[built-index-analytics]", step, error.message);
      }
    });
}
